import {
  Box, Card, CardContent, Chip, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import EventIcon from '@mui/icons-material/Event';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { aiSafety } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';

/**
 * AI Safety — events log.
 *
 * Pillar 10 audit trail. Every invocation of every clinical AI capability
 * lands here — this view is the cross-platform read of those events for
 * regulators, internal audit, and post-market monitoring.
 *
 * Privacy: we surface capability + model + clinician-side decision metadata,
 * but never the raw input/output (those live as hashes server-side and are
 * accessible only via a separate authorised endpoint that we don't ship in
 * the dashboard).
 */

type HumanAction = 'pending' | 'accepted' | 'edited' | 'rejected' | 'escalated';

type AIEvent = {
  id: number;
  capability: number;
  capability_code: string;
  model: number;
  model_name: string;
  patient: string | null;        // UUID — we only ever show first 8 chars
  clinician: number | null;
  appointment: string | null;
  input_hash: string;
  output_hash: string;
  output_summary: string;        // 1-line non-PHI summary the backend already filtered
  confidence: string | null;
  human_action: HumanAction;
  human_edited_output_hash: string | null;
  override_reason: string;
  latency_ms: number | null;
  created_at: string;
  decided_at: string | null;
};

type CapabilityRow = { id: number; code: string; display_name: string };

const ACTIONS: { value: HumanAction | 'all'; label: string }[] = [
  { value: 'all',       label: 'All decisions' },
  { value: 'pending',   label: 'Pending' },
  { value: 'accepted',  label: 'Accepted' },
  { value: 'edited',    label: 'Edited' },
  { value: 'rejected',  label: 'Rejected' },
  { value: 'escalated', label: 'Escalated' },
];

export function AISafetyEventsPage() {
  const [actionFilter, setActionFilter] = useState<HumanAction | 'all'>('all');
  const [capabilityFilter, setCapabilityFilter] = useState<string>('all');

  // The backend supports query params but we also filter client-side so the
  // user can change filters without a refetch — events list is small enough.
  const q = useApiQuery({
    queryKey: ['ai-safety', 'events'],
    queryFn:  () => aiSafety.events.list() as Promise<AIEvent[]>,
  });
  const capsQ = useApiQuery({
    queryKey: ['ai-safety', 'capabilities'],
    queryFn:  aiSafety.capabilities.list as () => Promise<CapabilityRow[]>,
  });

  const filtered = useMemo(() => {
    const all = q.data ?? [];
    return all.filter((e) =>
      (actionFilter === 'all' || e.human_action === actionFilter) &&
      (capabilityFilter === 'all' || e.capability_code === capabilityFilter)
    );
  }, [q.data, actionFilter, capabilityFilter]);

  return (
    <>
      <PageHeader
        title="AI events log"
        subtitle="Every AI invocation — capability, model, clinician decision, latency."
      />

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="Decision" sx={{ minWidth: 180 }}
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value as HumanAction | 'all')}
        >
          {ACTIONS.map((a) => (
            <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Capability" sx={{ minWidth: 240 }}
          value={capabilityFilter}
          onChange={(e) => setCapabilityFilter(e.target.value)}
        >
          <MenuItem value="all">All capabilities</MenuItem>
          {(capsQ.data ?? []).map((c) => (
            <MenuItem key={c.id} value={c.code}>
              {c.display_name} ({c.code})
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <DataState query={q} empty={{
        title: 'No AI events recorded yet',
        description: 'Once clinicians use AI-assisted features, every invocation lands here.',
      }}>
        {() => filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No events match these filters.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {filtered.map((e) => <Row key={e.id} event={e} />)}
          </Stack>
        )}
      </DataState>
    </>
  );
}

function Row({ event }: { event: AIEvent }) {
  const isProblem = event.human_action === 'rejected' || event.human_action === 'escalated';
  return (
    <Card sx={{ borderColor: isProblem ? 'warning.main' : 'divider' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            bgcolor: 'background.subtle',
            color: isProblem ? 'warning.main' : 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, mt: 0.25,
          }}>
            <EventIcon fontSize="small" />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center"
                   sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {event.capability_code}
              </Typography>
              <Chip size="small" variant="outlined" label={event.model_name}
                    sx={{ height: 20 }} />
              <ActionBadge action={event.human_action} />
              {event.confidence && (
                <Chip size="small" variant="outlined"
                      label={`conf ${formatConfidence(event.confidence)}`}
                      className="tabular" sx={{ height: 20 }} />
              )}
              {event.latency_ms != null && (
                <Chip size="small" variant="outlined"
                      label={`${event.latency_ms} ms`}
                      className="tabular" sx={{ height: 20 }} />
              )}
            </Stack>
            {event.output_summary && (
              <Typography variant="body2" sx={{ mb: 0.5 }} noWrap>
                {event.output_summary}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" className="tabular">
              {formatDateTime(event.created_at)}
              {event.patient && ` · pt ${event.patient.slice(0, 8)}`}
              {event.appointment && ` · appt ${event.appointment.slice(0, 8)}`}
              {event.decided_at && ` · decided ${formatDateTime(event.decided_at)}`}
            </Typography>
            {event.override_reason && (
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}>
                Override reason: {event.override_reason}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ActionBadge({ action }: { action: HumanAction }) {
  const config: Record<HumanAction, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
    pending:   { label: 'pending',   color: 'warning' },
    accepted:  { label: 'accepted',  color: 'success' },
    edited:    { label: 'edited',    color: 'success' },
    rejected:  { label: 'rejected',  color: 'default' },
    escalated: { label: 'escalated', color: 'error' },
  };
  const c = config[action];
  return <Chip size="small" label={c.label} color={c.color}
               variant="outlined" sx={{ height: 20 }} />;
}

function formatConfidence(c: string | null): string {
  if (!c) return '—';
  const n = parseFloat(c);
  if (Number.isNaN(n)) return '—';
  return `${Math.round(n * 100)}%`;
}
